import type { Join } from '@trello/action-history';
import { featureFlagClient } from '@trello/feature-flag-client';
import { client } from '@trello/graphql';
import { forNamespace } from '@trello/i18n';

import type { Card as LegacyCardModel } from 'app/scripts/models/Card';
import type { Trace } from '../ActionMapTypes';
import type { CardActionFragment } from '../CardActionFragment.generated';
import { CardActionFragmentDoc } from '../CardActionFragment.generated';
import { CardActionDocument } from '../CardActionMutation.generated';
import { NoopError } from '../NoopError';
import { onTracedCardUpdate } from '../onTracedCardUpdate';
import { recordCardAction } from '../recordCardAction';

const format = forNamespace('notificationsGrouped', {
  shouldEscapeStrings: false,
});

export const join = (card: LegacyCardModel, action: Join, trace: Trace) => {
  const useGraphQlActions = featureFlagClient.get(
    'xf.undo-redo-rewrite',
    false,
  );

  const cardId = card.id;
  const memberId = action.idMember;

  const { traceId } = trace;

  if (useGraphQlActions) {
    const cardFragment = client.readFragment<CardActionFragment>({
      id: `Card:${cardId}`,
      fragment: CardActionFragmentDoc,
    });

    if (!cardFragment) {
      return;
    }

    if (cardFragment.idMembers.includes(memberId)) {
      throw new NoopError();
    }

    const newMembers = [...cardFragment.idMembers, memberId];

    recordCardAction(cardId, action);

    client.mutate({
      mutation: CardActionDocument,
      variables: {
        cardId,
        card: {
          idMembers: newMembers,
        },
        traceId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCard: {
          __typename: 'Card',
          id: card.id,
          subscribed: true,
          idMembers: newMembers,
        },
      },
    });
  } else {
    if (card.hasMember(action.idMember)) {
      throw new NoopError();
    }
    card.addMemberWithTracing(
      action.idMember,
      traceId,
      onTracedCardUpdate({ card, ...trace }),
    );
  }

  return format('notification_joined_card');
};
